/**
 * nimbus-mobile-carousel.js
 * Simple scroll-snap carousel with dot indicators for mobile.
 * No infinite-loop cloning — clean, iOS-safe.
 */
(function () {
  'use strict';

  const BREAKPOINT = 749;

  function isMobile() { return window.innerWidth <= BREAKPOINT; }

  /* ──────────────────────────────────────────────────────────────
   * SbCarousel class
   * ────────────────────────────────────────────────────────────── */
  class SbCarousel {
    constructor(track, dotsWrap) {
      this.track           = track;
      this.dotsWrap        = dotsWrap;
      this.dots            = [];
      this.slides          = [];
      this.current         = 0;
      this._scrollEndTimer = null;
      this._onScroll       = this._onScroll.bind(this);
      this._onScrollEnd    = this._onScrollEnd.bind(this);
    }

    init(slides) {
      this.slides = slides;
      this._buildDots();
      this._bindScroll();
      // Set correct initial dot
      this._syncDots();
    }

    /* ── dots ───────────────────────────────────────────────── */
    _buildDots() {
      this.dotsWrap.innerHTML = '';
      this.dots = [];
      this.slides.forEach((_, i) => {
        const btn = document.createElement('button');
        btn.type      = 'button';
        btn.className = 'sb-carousel-dot';
        btn.setAttribute('aria-label', `Slide ${i + 1}`);
        btn.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        btn.addEventListener('click', () => this.goTo(i));
        this.dotsWrap.appendChild(btn);
        this.dots.push(btn);
      });
      this._setActive(0);
    }

    _setActive(i) {
      this.current = i;
      this.dots.forEach((d, idx) => {
        const active = idx === i;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    /* ── scroll binding ─────────────────────────────────────── */
    _bindScroll() {
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      if ('onscrollend' in window) {
        this.track.addEventListener('scrollend', this._onScrollEnd, { passive: true });
      } else {
        this.track.addEventListener('scroll', () => {
          clearTimeout(this._scrollEndTimer);
          this._scrollEndTimer = setTimeout(this._onScrollEnd, 200);
        }, { passive: true });
      }
    }

    /* ── find nearest slide based on scroll position ────────── */
    _nearestIndex() {
      const sl = this.track.scrollLeft;
      const tw = this.track.offsetWidth;
      let closest = 0, minDist = Infinity;
      this.slides.forEach((slide, i) => {
        const center = slide.offsetLeft + slide.offsetWidth / 2 - tw / 2;
        const dist   = Math.abs(center - sl);
        if (dist < minDist) { minDist = dist; closest = i; }
      });
      return closest;
    }

    _syncDots() {
      const idx = this._nearestIndex();
      if (idx !== this.current) this._setActive(idx);
    }

    /* ── handlers ───────────────────────────────────────────── */
    _onScroll() {
      this._syncDots();
    }

    _onScrollEnd() {
      this._syncDots();
    }

    /* ── public: dot click navigation ───────────────────────── */
    goTo(i) {
      const slide = this.slides[i];
      if (!slide) return;
      const center = slide.offsetLeft + slide.offsetWidth / 2 - this.track.offsetWidth / 2;
      this.track.scrollTo({ left: center, behavior: 'smooth' });
    }

    /* ── cleanup ────────────────────────────────────────────── */
    destroy() {
      this.track.removeEventListener('scroll', this._onScroll);
      this.track.removeEventListener('scrollend', this._onScrollEnd);
      clearTimeout(this._scrollEndTimer);
      this.dotsWrap.innerHTML = '';
      this.dots = [];
    }
  }

  /* ──────────────────────────────────────────────────────────────
   * Bootstrap
   * ────────────────────────────────────────────────────────────── */
  function getOrCreateDotsWrap(container) {
    let wrap = container.querySelector('.sb-carousel-dots');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'sb-carousel-dots';
      container.appendChild(wrap);
    }
    return wrap;
  }

  function initCarousels() {
    document.querySelectorAll('[data-sb-carousel]').forEach(container => {
      const track = container.querySelector('[data-sb-carousel-track]');
      if (!track) return;

      // Teardown previous instance
      if (track._sbCarousel) {
        track._sbCarousel.destroy();
        track._sbCarousel = null;
      }

      if (!isMobile()) return;

      const slides = Array.from(track.children)
        .filter(el => !el.classList.contains('sb-carousel-clone'));

      if (slides.length < 2) return;

      const dotsWrap = getOrCreateDotsWrap(container);
      const instance = new SbCarousel(track, dotsWrap);
      instance.init(slides);
      track._sbCarousel = instance;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(initCarousels, 200);
  }, { passive: true });

})();
